import {t} from "@lingui/core/macro";

import type {Result} from "@khanacademy/wonder-blocks-data";
import {
    JOIN_STUDENT_MUTATION,
    TextFieldWithInfo,
    type postloginJoinStudentMutationErrorCode,
    type postloginJoinStudentMutationType,
} from "@khan/auth-shared";
import {useMutation} from "@khan/data-gql";
import type {Location} from "@khan/urls";

import {validateClassCode} from "./validate-class-code.ts";

interface Props {
    /**
     * The label for the field.
     */
    label?: string;
    /**
     * The placeholder text for the field.
     */
    placeholder?: string;
    /**
     * If the field is required.
     */
    required?: boolean;
    /**
     * The html id for the field.
     */
    id?: string;
    /**
     * The current value of the field.
     */
    value: string;
    /**
     * The function to call when the field changes.
     */
    onChange: (value: string) => void;
    /**
     * The error message to display.
     */
    error: string | null | undefined;
    /**
     * The function to call when the field is validated.
     */
    onValidate?: (errorMessage?: string | null | undefined) => unknown;
}

/**
 * A field for entering a class code. This component will perform validation
 * on the input and pass an error to `onValidate`. If the error prop is not
 * null, it will be displayed and the field will be marked as invalid.
 */
export function ClassCodeField({
    label = t`Have a class code? Join here!`,
    placeholder = t`Eg. G09WP`,
    required = false,
    value,
    onChange,
    error,
    onValidate,
    id = "class-code",
}: Props) {
    return (
        <TextFieldWithInfo
            id={id}
            required={required}
            value={value}
            placeholder={placeholder}
            onChange={(newVal) => {
                onChange(newVal.toUpperCase().trim());
            }}
            validate={(value) => validateClassCode(value)}
            errorMessage={error}
            onValidate={onValidate}
            label={label}
        />
    );
}

/**
 * Hook for the current user to join a class.
 */
// eslint-disable-next-line react-refresh/only-export-components -- used elsewhere
export function useJoinClassMutation() {
    return useMutation(JOIN_STUDENT_MUTATION);
}

/**
 * Extracts the error message from the result of `useJoinClassMutation`.
 * Should be called by the parent and the result passed into the error prop.
 */
// eslint-disable-next-line react-refresh/only-export-components -- used elsewhere
export function extractErrorFromJoinClassResult(
    classCode: string,
    result:
        | Result<postloginJoinStudentMutationType["response"]>
        | null
        | undefined,
): string | null {
    if (!result) {
        return null;
    }

    if (result.status === "success") {
        if (result.data?.joinStudent?.error) {
            const errCode = result.data.joinStudent.error.code;
            return mapErrCodeToMessage(errCode, classCode);
        }
        return null;
    }
    if (result.status === "error") {
        return buildErrorMessage(result.error.message);
    }
    if (result.status === "no-data" || result.status === "aborted") {
        return buildErrorMessage();
    }

    return null;
}

function buildErrorMessage(extraMessage: string = ""): string {
    return t`Something went wrong, please try again. ${extraMessage}`;
}

function mapErrCodeToMessage(
    errCode: postloginJoinStudentMutationErrorCode,
    classCode: string,
): string {
    switch (errCode) {
        case "CLASSROOM_NOT_FOUND":
            return t`Classroom with code "${classCode}" not found. Please double check and try again`;
        case "DISTRICT_SYNCED_CLASSROOM":
            return t`District synced classes cannot be joined by class code without activating the student`;
        case "CANNOT_ADD_COACH":
            return t`Cannot join this coach's class`;
        default:
            return t`Failed to join class due to: $(errCode)s`;
    }
}

/**
 * Looks for a "classcode" query param, and if found, validates and returns it.
 * @param {Location} location a Location obj from `useLocation`
 **/
// eslint-disable-next-line react-refresh/only-export-components -- used elsewhere
export function getClassCodeFromLocation(location: Location): string | null {
    const currUrl = new URL(location.href);
    const classCodeParam = currUrl.searchParams.get("classcode");
    // If there's a class code which starts with 8 letters or numbers...
    if (classCodeParam && classCodeParam.match(/^[A-Za-z0-9]{8}/)) {
        return classCodeParam.slice(0, 8);
    }

    return null;
}
